import { create } from "zustand";
import { io } from "socket.io-client";
import { Subject, BehaviorSubject } from "rxjs";
import { filter } from "rxjs/operators";
import keycloak from "../keycloak";
import api from "../services/api";

const useSocketStore = create((set, get) => {
  const messageSubject = new Subject();
  const isConnected$ = new BehaviorSubject(false);
  let socket = null;
  let eventsRegistered = new Set();

  return {
    socket: null,
    isConnected: false,

    connect: async (token, window) => {
      if (!socket) {
        try {
          await keycloak.updateToken(30);
        } catch {
          keycloak.login();
          return;
        }
        socket = io(process.env.REACT_APP_BASE_URL_WS, {
          autoConnect: false,
          extraHeaders: { authorization: `Bearer ${keycloak.token}` },
        });

        socket.on("connect", () => {
          console.log("WebSocket connected");
          set({ isConnected: true });
          isConnected$.next(true);
        });

        socket.on("disconnect", () => {
          console.log("WebSocket disconnected");
          set({ isConnected: false });
          isConnected$.next(false);
          socket = null;
          eventsRegistered.clear();
        });

        socket.on("connect_error", (error) => {
          console.error("Socket.io connection error:", error);
          if (socket && socket.connected) {
            socket.disconnect();
          }
          window.location.assign("/");
        });

        socket.on("error", (error) => {
          console.error("Socket.io error:", error);

          const code = error?.code || error?.response?.statusCode;

          // Erros de autenticação: desconectar e redirecionar
          if (
            code === "INVALID_TOKEN" ||
            code === "TOKEN_MISSING" ||
            code === "UNAUTHORIZED" ||
            code === "USER_LOCKED" ||
            code === "TRIAL_EXPIRED"
          ) {
            if (socket && socket.connected) {
              socket.disconnect();
            }
            window.location.assign("/");
            return;
          }

          // Erros operacionais (assistant not found, falha de processamento):
          // apenas loga, não mata a conexão
          console.warn("Non-fatal WebSocket error:", error);
        });

        // TODO: Melhorar tratamento de erros

        socket.connect();
        set({ socket });
      } else if (socket) {
        console.log("WebSocket already connected");
      }
    },

    disconnect: () => {
      if (socket && socket.connected) {
        socket.disconnect();
      }
    },

    sendMessage: async (message) => {
      await get().ensureConnected();
      get().socket.emit("message", message);
    },

    sendContinueMessage: async (chatId, token) => {
      await get().ensureConnected();
      get().socket.emit("continue-generating", { chatId, token });
    },

    sendSystemContextHTTP: async (chatId, context) => {
      try {
        await api.post(
          `${process.env.REACT_APP_AUTH_API_URL}/chats/add-context/${chatId}`,
          { message: context }
        );
        console.log("System context sent successfully");
      } catch (error) {
        console.error("Failed to send system context:", error);
      }
    },

    onMessage: (event, callback) => {
      const registerEvent = () => {
        if (!eventsRegistered.has(event) && socket) {
          eventsRegistered.add(event);
          socket.on(event, (data) => {
            messageSubject.next({ event, data });
          });
        }
      };

      registerEvent();

      // If socket is not ready yet, register once it connects
      if (!socket) {
        const sub = isConnected$.subscribe((connected) => {
          if (connected) {
            registerEvent();
            sub.unsubscribe();
          }
        });
      }

      const subscription = messageSubject
        .pipe(filter(({ event: e }) => e === event))
        .subscribe(({ data }) => callback(data));

      return () => {
        eventsRegistered.delete(event);
        if (socket && socket.connected) socket.off(event);
        subscription.unsubscribe();
      };
    },
    ensureConnected: async () => {
      const { isConnected } = get();

      console.log("ensureConnected called. Is connected:", isConnected);

      if (!isConnected) {
        await new Promise((resolve) => {
          const subscription = isConnected$.subscribe((connected) => {
            if (connected) {
              resolve();

              subscription.unsubscribe();
            }
          });
        });
      }
    },
  };
});

export default useSocketStore;
